import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AUTH, NAV } from "@/constants/testIds";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "recruiter", org: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to TECHAIFORTUNE!");
      nav("/app");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Registration failed";
      setErr(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block bg-[#09090B] bg-grid-dark relative">
        <Link to="/" data-testid={NAV.brandLink} className="absolute top-12 left-12 flex items-center gap-2 text-white">
          <img src="/logo-mark.png" alt="" className="h-9 w-9" />
          <span className="font-display font-bold tracking-tight">TechAI Fortune</span>
        </Link>
        <div className="absolute inset-0 grid place-items-center p-12">
          <div className="text-white">
            <div className="text-xs uppercase tracking-[0.3em] text-white/60 mb-4">Get started free</div>
            <h2 className="font-display text-5xl font-bold tracking-tight max-w-md leading-[1.05]">
              14 days. Full platform. <span className="text-[#4338CA]">Seeded workspace.</span>
            </h2>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center p-8 lg:p-16">
        <div className="max-w-md w-full mx-auto">
          <h1 className="font-display text-4xl font-bold tracking-tight">Create account</h1>
          <p className="mt-2 text-muted-foreground">Spin up your workspace in seconds.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid={AUTH.registerName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-2 h-11" />
            </div>
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" data-testid={AUTH.registerEmail} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-2 h-11" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid={AUTH.registerPassword} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className="mt-2 h-11" />
            </div>
            <div>
              <Label>I am a…</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid={AUTH.registerRole} className="mt-2 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recruiter">Recruiter</SelectItem>
                  <SelectItem value="client">Hiring Manager (Client)</SelectItem>
                  <SelectItem value="candidate">Candidate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="org">Organization (optional)</Label>
              <Input id="org" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} className="mt-2 h-11" />
            </div>
            {err && <div className="text-sm text-destructive">{err}</div>}
            <Button type="submit" data-testid={AUTH.registerSubmit} disabled={loading} className="w-full h-11 bg-[#0047AB] hover:bg-[#003380]">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
          <p className="mt-8 text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-[#0047AB] hover:underline font-medium">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
